import { paramsSerializer } from "@core/helpers"
import { ServiceError } from "@core/exceptions"
import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"

// =====================
// Type definitions des réponses des services
// =====================

/** Résultat GET export DAPI : fichier immédiat ou tâche Celery (303 GoAsync / 200 async). */
export type ExportDapiOutcome =
    | { mode: "sync"; fileUrl: string }
    | { mode: "async"; taskId: string }

function getValidateStatusDapiExport(): (status: number) => boolean {
    return (status: number) => (status >= 200 && status < 300) || status === 303
}

/**
 * Interprète le corps JSON des routes /export/building et /export/source.
 */
function parseExportDapiOutcome(
    status: number,
    body: unknown
): ExportDapiOutcome {
    if (!body || typeof body !== "object") {
        throw new ServiceError(
            {
                message: "Réponse d'export invalide",
                code: "export_invalid_body",
            },
            status
        )
    }
    const b = body as Record<string, unknown>

    if (status === 303) {
        const asyncData = b.data as Record<string, unknown> | undefined
        const taskId = asyncData?.task_id
        if (typeof taskId === "string" && taskId.length > 0) {
            return { mode: "async", taskId }
        }
        throw new ServiceError(
            {
                message: "Export asynchrone sans identifiant de tâche",
                code: "export_async_missing_task_id",
            },
            303
        )
    }

    if (typeof b.file_url === "string" && b.file_url.length > 0) {
        return { mode: "sync", fileUrl: b.file_url }
    }

    const nested = b.data as Record<string, unknown> | undefined
    if (nested) {
        if (typeof nested.task_id === "string" && nested.task_id.length > 0) {
            return { mode: "async", taskId: nested.task_id }
        }
        if (typeof nested.file_url === "string" && nested.file_url.length > 0) {
            return { mode: "sync", fileUrl: nested.file_url }
        }
    }

    throw new ServiceError(
        {
            message: "Réponse d'export inattendue",
            code: "export_unexpected_response",
        },
        status
    )
}

export type GetExportBuildingParams = {
    _id_building: string
    date_from: string
    date_to: string
    variables: string[]
    freqs: string[]
    _id_user: string
}

export type ExportSourceParams = {
    _id_source: string
    date_from: string
    date_to: string
    variables: string[]
    freqs: string[]
    _id_user: string
}

export type ExportR63Params = {
    _id_source: string
    date_from: string
    date_to: string
}

// =====================
// Service
// =====================

/**
 * Service d'export des données (bâtiment, source, R63, etc.).
 */
export class ExportService {
    /**
     * Génère un export des données du bâtiment.
     * GET /api/dapi/export/building — sync : `file_url` ; volume élevé : 303 GoAsync ou 200 `{ data: { task_id } }`.
     * @param params - Paramètres de l'export (bâtiment, période, variables, fréquences, utilisateur)
     * @param abortController - Contrôleur d'annulation
     */
    async getExportBuilding(
        params: GetExportBuildingParams,
        abortController?: AbortController
    ): Promise<ExportDapiOutcome> {
        return handleServiceCall<ExportDapiOutcome>(async () => {
            const { status, data: body } = await axios.get<unknown>(
                "/api/dapi/export/building",
                {
                    params,
                    paramsSerializer: paramsSerializer,
                    signal: abortController?.signal,
                    validateStatus: getValidateStatusDapiExport(),
                }
            )
            return parseExportDapiOutcome(status, body)
        })
    }

    /**
     * Export des données d'une source. GET /api/dapi/export/source
     */
    async exportSource(
        params: ExportSourceParams,
        abortController?: AbortController
    ): Promise<ExportDapiOutcome> {
        return handleServiceCall<ExportDapiOutcome>(async () => {
            const { status, data: body } = await axios.get<unknown>(
                "/api/dapi/export/source",
                {
                    params,
                    paramsSerializer: paramsSerializer,
                    signal: abortController?.signal,
                    validateStatus: getValidateStatusDapiExport(),
                }
            )
            return parseExportDapiOutcome(status, body)
        })
    }

    /**
     * Export R63 (PDL). GET /api/dapi/export/r63
     * @returns URL du fichier (file_url)
     */
    async exportR63(
        params: ExportR63Params,
        abortController?: AbortController
    ): Promise<string> {
        return handleServiceCall<string>(async () => {
            const {
                data: { file_url },
            } = await axios.get<{ file_url: string }>("/api/dapi/export/r63", {
                params,
                paramsSerializer: paramsSerializer,
                signal: abortController?.signal,
            })
            return file_url
        })
    }
}
